import Select, { Async as AsyncSelect } from "react-select";
import t from "prop-types";
import { Tooltip } from "@material-ui/core";
import HelpIcon from "@material-ui/icons/Help";


import { css } from "@emotion/react";

/* Based on react-select.
in order to better support django manyToMany relationships
we're transfering flat id values instead of object
*/

const rootStyle = theme =>
  css`
    display: inline-block;
    position: relative;
    width: 100%;

    &.-disabled svg {
      opacity: 0.4;
    }

    &.limit-height .react-select__control {
      max-height: 150px;
      overflow-y: auto;
    }

    .select-label-div {
      font-size: 12px;
      color: ${theme.colors.text.secondary};
      position: absolute;
      z-index: 20;
      top: -10px;
      right: 0;
      background: linear-gradient(180deg, transparent 8%, rgba(255,255,255,1) 50%);
      padding: 0 4px;
      white-space: nowrap;
    }
    .select-label-div.select-label-div-disabled {
      background: linear-gradient(180deg, transparent 8%, #f2f2f2 50%);
    }
    
    .select-label-help-icon
    {
      position:relative;
      font-size: 1rem;
      top: 0.15rem;
    }

    .react-select__indicators { height: 35px; }
    .react-select__control {
      min-height: 38px;
      &:not(&--is-disabled) {
        border-color: ${theme.colors.text.secondary};
      }
    }

    .react-select__control--is-focused {
      border-color: ${theme.colors.primary.main} !important;
      box-shadow: 0 0 0 1px ${theme.colors.primary.main} inset !important;
    }

    &.select-container-borderless, &.select-container-blue {
      .react-select__dropdown-indicator, .react-select__placeholder {
        color: ${theme.colors.text.secondary};
      }

      .react-select__indicator-separator {
        display: none;
      }

      .react-select__control, .react-select__control *, .react-select__control--is-focused {
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
      }
    }

    &.select-container-blue {
      .react-select__control {         
        background-color: ${theme.colors.surface.main};
        border-radius: 12px;

        &.react-select__control--is-disabled {
          opacity: 0.75;
        }
      }
    } 

    &.select-container-rounded { 
      .react-select__control {
        border-radius: 12px;
      }

      .react-select__indicator-separator {
        display: none;
      }
    }

    .react-select__multi-value.indi-chip {
      left: 0 !important;
    }

    &.with-icon {
      > span > svg, > svg { 
        position: absolute; 
        z-index: 1;
        pointer-events: none;
        top: 5px;
        left: 6px;
        padding: 1px;
      }

      .react-select__value-container {
        padding-left: 34px;
      }
    }

    .react-select__menu-list {
      color: ${theme.colors.text.main};
      .react-select__option {
        &:not(.react-select__option--is-selected) {
          &:hover {
            background-color: ${theme.colors.surface.light};
          }
        }
        &--is-selected {
          background-color: ${theme.colors.primary.main};
        }
      }
    }

    .react-select__single-value {
      color: ${theme.colors.text.main};
    }
    .react-select__single-value--is-disabled {
      opacity: .6;
    }

    .react-select__option {
      background-color: transparent;
    }

  `;

const labelStyle = {
  fontSize: "1rem",
  transform: "translate(0, 1.5px) scale(0.75)",
  transformOrigin: "top left",
};

const CommonSelect = passedProps => {
  const props = { ...passedProps };

  let selectedValues;
  let flatOnClick;
  let isMaxSize;

  let options = props.formatGroupLabel || props.isOptionsGrouped ? (props.options || []).reduce((res, item) => res.concat(item.options), []) : props.options;
  if (props.isAsync) {
    options = props.formatGroupLabel || props.isOptionsGrouped
      ? [...props.defaultOptions, ...props.afterAsyncOptions || []].reduce((res, item) => res.concat(item.options), [])
      : [...props.defaultOptions, ...props.afterAsyncOptions || []];
  }
  const normalizedOptions = (options || []).reduce((result, item) => { result[item.value] = item; return result; }, {});
  if (!props.onChange) { props.onChange = () => { }; }

  if (props.isMulti) {
    // eslint-disable-next-line no-nested-ternary, max-len
    selectedValues = props.value || props.value === 0 ? (Array.isArray(props.value) ? props.value.map(item => (normalizedOptions[item])) : [normalizedOptions[props.value]]) : undefined;
    flatOnClick = (items) => props.onChange(items.map(item => item.value));
    if (props.asyncMaxSelectSize) {
      props.defaultOptions = props.asyncMaxSelectSize <= selectedValues.length ? [] : props.defaultOptions;
    }
    isMaxSize = props.maxSelectValues <= (props.value?.length || 0);
    if (isMaxSize && props.value) {
      props.options = props.value.map(item => normalizedOptions[item]);
    }
  } else {
    selectedValues = (props.value || props.value === 0) ? normalizedOptions[props.value] : props.value;
    flatOnClick = (item) => props.onChange(item ? item.value : null);
  }
  let mainClassName = "select-container";
  if (props.limitHeight) { mainClassName += " limit-height"; }
  if (props.variant) { mainClassName += ` select-container-${props.variant}`; }
  if (props.icon) { mainClassName += " with-icon"; }
  if (props.disabled || props.isDisabled) { mainClassName += " -disabled"; }

  return (
    <div css={rootStyle} className={mainClassName} data-testid={props.testId}>
      {props.label && (
      <div className={`select-label-div ${props.disabled || props.isDisabled ? "select-label-div-disabled" : ""}`} style={labelStyle}>{props.label} {(props.mandatory || props.required) && <em style={{ color: "red" }}>*</em>}
        {props.tooltipTxt
        && (
        <Tooltip
          placement="top"
          title={props.tooltipTxt || ""}
          disableHoverListener={!props.tooltipTxt}
          enterDelay={500}
        >
          <span>
          &nbsp;&nbsp;
            <HelpIcon className="select-label-help-icon" />
          </span>
        </Tooltip>
        )}
      </div>
      )}
      {!!props.icon && props.icon}
      { props.isAsync
        ? (
          <AsyncSelect
            placeholder={props.placeholder || ""}
            classNamePrefix="react-select"
            isDisabled={props.disabled || props.isDisabled}
            isClearable
            closeMenuOnSelect={!props.isMulti}
            menuPosition="fixed"
            {...props}
            value={selectedValues}
            onChange={flatOnClick}
            tabSelectsValue={false}
          />
        )
        : (
          <Select
            placeholder={props.placeholder || ""}
            classNamePrefix="react-select"
            isDisabled={props.disabled || props.isDisabled}
            isClearable
            closeMenuOnSelect={!props.isMulti}
            menuPosition="fixed"
            {...props}
            value={selectedValues}
            onChange={flatOnClick}
            tabSelectsValue={false}
          />
        )}
    </div>
  );
};

CommonSelect.propTypes = {
  value: t.oneOfType([t.array, t.string, t.number]),
  onChange: t.func,
  tooltipTxt: t.string,
  limitHeight: t.bool,
  testId: t.string,
  isAsync: t.bool,
  isMulti: t.bool,
  disabled: t.bool,
  isDisabled: t.bool,
  label: t.string,
  mandatory: t.bool,
  required: t.bool,
  formatGroupLabel: t.func,
  options: t.array,
  defaultOptions: t.array,
  placeholder: t.oneOfType([t.string, t.object]),
  afterAsyncOptions: t.array,
  isOptionsGrouped: t.bool,
  asyncMaxSelectSize: t.number,
  variant: t.string,
  icon: t.element,
  maxSelectValues: t.number, // similar to asyncMaxSelectSize for synchronuous select
};
export default CommonSelect;
